import { ConfirmDialogComponent } from "@/components/confirm-dialog/confirm-dialog.component";
import { InfoDialogComponent } from "@/components/info-dialog/info-dialog.component";
import { SubscriptionExtendDialogComponent } from "@/components/subscription-extend-dialog/subscription-extend-dialog.component";
import { DateTimeFormatPipe } from "@/extensions/dateTimePipe";
import { AppConstants } from "@/helpers/app.constants";
import { UserHelper } from "@/helpers/user.helper";
import { Paging } from "@/models/Paging";
import { AlertService } from "@/services/alert.service";
import { formatDate, NgIf } from "@angular/common";
import {
  AfterViewInit,
  ChangeDetectorRef,
  Component,
  Inject,
  LOCALE_ID,
  OnDestroy,
  OnInit,
  ViewChild,
} from "@angular/core";
import {
  FormControl,
  FormsModule,
  ReactiveFormsModule,
  UntypedFormBuilder,
} from "@angular/forms";
import { MatButtonModule } from "@angular/material/button";
import { MatDialog } from "@angular/material/dialog";
import { MatDividerModule } from "@angular/material/divider";
import { MatExpansionModule } from "@angular/material/expansion";
import { MatIconModule } from "@angular/material/icon";
import { MatMenuModule } from "@angular/material/menu";
import { MatPaginator, MatPaginatorModule } from "@angular/material/paginator";
import { MatProgressSpinnerModule } from "@angular/material/progress-spinner";
import { MatSlideToggleModule } from "@angular/material/slide-toggle";
import { MatSort, MatSortModule } from "@angular/material/sort";
import { MatTableDataSource, MatTableModule } from "@angular/material/table";
import {
  BusinessManagementService,
  SubscriptionManagementType,
  SubscriptionPlanType,
  SubscriptionState,
  Subscription as SubscriptionUser,
  UsersService,
} from "@eemo/api-client";
import { TranslateService } from "@ngx-translate/core";
import {
  catchError,
  map,
  merge,
  of as observableOf,
  startWith,
  Subscription,
  switchMap,
} from "rxjs";
import { environment } from "src/environments/environment";
import { SelectComponent } from "../../../components/ui-forms/select/select.component";
import { DateFormatPipe } from "../../../extensions/datePipe";

import { InputComponent } from "@/components/ui-forms/input/input.component";

@Component({
  selector: "app-admin-subscriptions",
  templateUrl: "./admin-subscriptions.component.html",
  styleUrls: ["./admin-subscriptions.component.scss"],
  standalone: true,
  imports: [
    FormsModule,
    ReactiveFormsModule,
    InputComponent,
    MatExpansionModule,
    SelectComponent,
    MatSlideToggleModule,
    MatDividerModule,
    NgIf,
    MatProgressSpinnerModule,
    MatTableModule,
    MatSortModule,
    MatIconModule,
    MatButtonModule,
    MatMenuModule,
    MatPaginatorModule,
    DateFormatPipe,
    DateTimeFormatPipe,
  ],
})
export class AdminSubscriptionsComponent
  implements OnInit, AfterViewInit, OnDestroy
{
  @ViewChild(MatPaginator) paginator: MatPaginator;
  @ViewChild(MatSort) sort: MatSort;

  dataSource: MatTableDataSource<SubscriptionUser>;
  displayFullColumns = false;
  lightColumns = [
    "user",
    "state",
    "dateEnd",
    "planType",
    "lastConnection",
    "actions",
  ];
  fullColumns = [
    "user",
    "state",
    "planType",
    "dateStart",
    "dateEnd",
    "dateCreate",
    "lastConnection",
    "managementType",
    "events",
    "externalId",
    "uid",
    "actions",
  ];
  displayedColumns = [];
  resultsLength = 0;
  isLoadingResults = true;

  search: string;
  startDateInvalid = false;
  endDateInvalid = false;
  filterStartDateInput: string;
  filterEndDateInput: string;

  availablePlanTypes: SubscriptionPlanType[];
  availableStates: SubscriptionState[];

  sub = new Subscription();
  cancelSub = new Subscription();
  addTrialSub = new Subscription();
  addOfferedSub = new Subscription();
  userSub = new Subscription();

  paging = new Paging();

  defaultStateFilter = [
    SubscriptionState.ACTIVE,
    SubscriptionState.EXPIRED,
    SubscriptionState.SUSPENDED,
    SubscriptionState.TRIALING,
  ];

  get form_name() {
    return this.form.get("name") as FormControl<string>;
  }
  get form_planType() {
    return this.form.get("planType") as FormControl<SubscriptionPlanType>;
  }
  get form_state() {
    return this.form.get("state") as FormControl<SubscriptionState>;
  }

  form = this.fb.group({
    name: [null],
    planType: [null],
    state: [null],
  });

  constructor(
    private fb: UntypedFormBuilder,
    private businessService: BusinessManagementService,
    private userService: UsersService,
    private alerteService: AlertService,
    private translate: TranslateService,
    private dialog: MatDialog,
    private alertService: AlertService,
    private changeDetectorRefs: ChangeDetectorRef,
    @Inject(LOCALE_ID) public locale: string
  ) {
    this.displayedColumns = this.lightColumns;
  }

  ngOnInit() {
    this.availablePlanTypes = Object.keys(
      SubscriptionPlanType
    ) as SubscriptionPlanType[];
    this.availableStates = Object.keys(
      SubscriptionState
    ) as SubscriptionState[];
  }

  ngOnDestroy(): void {
    this.sub.unsubscribe();
    this.cancelSub.unsubscribe();
    this.addTrialSub.unsubscribe();
    this.addOfferedSub.unsubscribe();
    this.userSub.unsubscribe();
  }

  ngAfterViewInit() {
    // If the user changes the sort order, reset back to the first page.
    this.sort.sortChange.subscribe(() => (this.paginator.pageIndex = 0));
    this.loadData();
  }

  loadData() {
    this.sub = merge(this.sort.sortChange, this.paginator.page)
      .pipe(
        startWith({}),
        switchMap(() => {
          this.isLoadingResults = true;
          return this.businessService.getSubscriptionList(
            this.search,
            [this.form_planType.value],
            this.form_state.value
              ? [this.form_state.value]
              : this.defaultStateFilter,
            this.paginator.pageIndex * this.paginator.pageSize,
            this.paginator.pageSize
          );
        }),
        map((dataList) => {
          // Flip flag to show that loading has finished.
          this.isLoadingResults = false;
          this.resultsLength = dataList.paging.totalItems;
          this.alerteService.clear();
          return dataList.list;
        }),
        catchError(() => {
          this.isLoadingResults = false;
          return observableOf([]);
        })
      )
      .subscribe((data) => {
        const displayedData = [];
        data.forEach((i: SubscriptionUser) => {
          const row = {
            subscription: i,
            uid: i.uid,
            planType: this.translate.instant(
              "enum.SubscriptionPlanType." + i.planType
            ),
            managementType: this.translate.instant(
              "enum.SubscriptionManagementType." + i.managementType
            ),
            state: this.translate.instant("enum.SubscriptionState." + i.state),
            dateCreate: i.dateCreate,
            dateStart: i.dateStart,
            dateEnd: i.dateEnd,
            user: i.user
              ? i.user.name
                ? UserHelper.userSummmaryDisplayName(i.user)
                : i.user.uid
              : "",
            userId: i.user.uid,
            externalId: i.externalId,
            lastConnection: i.userLastConnectionDate,
            canBeCanceled:
              i.managementType === SubscriptionManagementType.LOCAL &&
              i.state !== SubscriptionState.CANCELED,
            isLocalTrial: i.planType === SubscriptionPlanType.TRIAL,
            canBeAdded: i.state === SubscriptionState.CANCELED,
            canBeOffered: i.planType === SubscriptionPlanType.TRIAL,
            canBeExtended:
              i.planType === SubscriptionPlanType.TRIAL &&
              i.state !== SubscriptionState.CANCELED,
            stripeUrl:
              i.managementType === SubscriptionManagementType.STRIPE
                ? environment.stripeUrl + "/subscriptions/" + i.externalId
                : null,
          };
          displayedData.push(row);
        });
        this.dataSource = new MatTableDataSource(displayedData);
      });
  }

  applyFilter() {
    const filterValue = this.form_name.value;
    if (!filterValue || filterValue.length > 1) {
      this.search = filterValue ? filterValue.trim().toLowerCase() : null;
      this.paginator.pageIndex = 0;
      this.loadData();
    }
  }

  updateDataFromFilters() {
    this.paginator.pageIndex = 0;
    this.loadData();
  }

  toggleDisplayColumns() {
    if (this.displayFullColumns) {
      this.displayFullColumns = false;
      this.displayedColumns = this.lightColumns;
    } else {
      this.displayFullColumns = true;
      this.displayedColumns = this.fullColumns;
    }
    this.changeDetectorRefs.detectChanges();
  }

  showEvents(sub: SubscriptionUser) {
    // prevents to open multiple dialog
    if (this.dialog.openDialogs.length === 0) {
      let content = "<ul>";
      sub.events.forEach((e) => {
        content +=
          "<li>" +
          formatDate(e.date, AppConstants.DATE_TIME_FORMAT, this.locale) +
          " - " +
          e.type +
          (e.details ? " - " + e.details : "") +
          "</li>";
      });
      content += "</ul>";
      const dialogRef = this.dialog.open(InfoDialogComponent, {
        maxWidth: "90vw",
        data: {
          title: "Détail de la stacktrace",
          okButtonText: "OK",
          content: content,
        },
      });
    }
  }

  confirmCancel(sub: SubscriptionUser, user: string) {
    // prevents to open multiple dialog
    if (this.dialog.openDialogs.length === 0) {
      const dialogRef = this.dialog.open(ConfirmDialogComponent, {
        // width: '25rem',
        data: {
          message:
            "Confirmez-vous la suppression de l'abonnement de \"" +
            user +
            '" ?',
        },
      });
      dialogRef.afterClosed().subscribe((result) => {
        if (result) {
          this.doCancel(sub);
        }
      });
    }
  }

  doCancel(sub: SubscriptionUser) {
    this.cancelSub = this.businessService
      .cancelSubscription(sub.uid)
      .subscribe((result) => {
        this.alertService.success("Suppression effectuée avec succès");
        this.loadData();
      });
  }

  goExtendTrial(sub: SubscriptionUser) {
    // prevents to open multiple dialog
    if (this.dialog.openDialogs.length === 0) {
      const dialogRef = this.dialog.open(SubscriptionExtendDialogComponent, {
        // width: '25rem',
        data: { sub },
      });
      dialogRef.afterClosed().subscribe((result) => {
        if (result) {
          this.loadData();
        }
      });
    }
  }

  confirmAddTrialPlan(userId: string, user: string) {
    // prevents to open multiple dialog
    if (this.dialog.openDialogs.length === 0) {
      const dialogRef = this.dialog.open(ConfirmDialogComponent, {
        // width: '25rem',
        data: {
          message:
            "Confirmez-vous l'ajout d'un l'abonnement d'essai pour \"" +
            user +
            '" ?',
        },
      });
      dialogRef.afterClosed().subscribe((result) => {
        if (result) {
          this.addTrialPlan(userId);
        }
      });
    }
  }

  addTrialPlan(userId: string) {
    this.addTrialSub = this.businessService
      .addUserSubscription(userId, SubscriptionPlanType.TRIAL)
      .subscribe((result) => {
        this.alertService.success("Abonnement ajouté");
        this.loadData();
      });
  }

  confirmAddOfferedPlan(userId: string, user: string) {
    // prevents to open multiple dialog
    if (this.dialog.openDialogs.length === 0) {
      const dialogRef = this.dialog.open(ConfirmDialogComponent, {
        // width: '25rem',
        data: {
          message:
            "Confirmez-vous l'ajout d'un l'abonnement offert pour \"" +
            user +
            '" ?',
        },
      });
      dialogRef.afterClosed().subscribe((result) => {
        if (result) {
          this.addOfferedPlan(userId);
        }
      });
    }
  }

  addOfferedPlan(userId: string) {
    this.addTrialSub = this.businessService
      .addUserSubscription(userId, SubscriptionPlanType.OFFERED)
      .subscribe((result) => {
        this.alertService.success("Abonnement ajouté");
        this.loadData();
      });
  }

  showUserInfo(userId: string) {
    this.userSub = this.userService.getUser(userId).subscribe((u) => {
      const contentPhone = u.phoneNumber
        ? `<a href="tel:${u.phoneNumber}">${u.phoneNumber}</a>`
        : "Non renseigné";
      this.dialog.open(InfoDialogComponent, {
        // width: '25rem',
        data: {
          title: UserHelper.userDisplayName(u),
          content: `<p><span class="info-title">Email:</span><a href="mailto:${u.email}">${u.email}</a></p>
            <p><span class="info-title">Tel:</span>${contentPhone}</p>
            <p><span class="info-title">Role:</span>${u.role}</p>
            <p><span class="info-title">Active:</span>${u.active}</p>
            <p><span class="info-title">uid:</span>${u.uid}</p>`,
          okButtonText: "Fermer",
        },
      });
    });
  }
}
